import { useI18n } from '@/i18n/i18n-context'
import type { SubmitScanRequest } from '@/api/types'
import type { TranslationKey } from '@/i18n/translations'
import { Card } from '@/ui/card'

/**
 * What was measured, in the order a patient reads it, and ONLY what was measured.
 *
 * Each row is present exactly when its value is: the submit shape omits what the vendor did not
 * produce (to-submit-request.ts), so a scan that got a pulse and nothing else shows one row rather
 * than four rows of blanks. Blood pressure is one row because it is one reading.
 *
 * `hypertensionClassification` is deliberately not among them. It is the one field on the request
 * that is an INTERPRETATION rather than a measurement, and this service applies none - a rule with a
 * CI check behind it (scripts/check-no-clinical-logic.sh). A number a patient can take to their care
 * team is not the same thing as a category this page has decided they belong in.
 */
function measurements(results: SubmitScanRequest): readonly { key: TranslationKey; value: string }[] {
  const rows: { key: TranslationKey; value: string }[] = []

  if (results.hr > 0) rows.push({ key: 'heartRate', value: `${results.hr} bpm` })
  if (results.sbp && results.dbp) {
    rows.push({ key: 'bloodPressure', value: `${results.sbp}/${results.dbp} mmHg` })
  }
  if (results.spo2) rows.push({ key: 'bloodOxygen', value: `${results.spo2}%` })
  if (results.rr) rows.push({ key: 'breathing', value: `${results.rr}/min` })

  return rows
}

export function CompleteScreen({ results }: { readonly results?: SubmitScanRequest }) {
  const { t } = useI18n()
  const rows = results ? measurements(results) : []

  return (
    <Card className="flex flex-col gap-3 text-center">
      <h1 className="text-lg font-semibold text-slate-900">{t('scanComplete')}</h1>

      {rows.length > 0 && (
        <dl className="my-1 divide-y divide-slate-200 rounded-lg border border-slate-200 text-left">
          {rows.map((row) => (
            <div key={row.key} className="flex items-baseline justify-between gap-4 px-4 py-3">
              <dt className="text-sm text-slate-600">{t(row.key)}</dt>
              <dd className="text-lg font-semibold tabular-nums text-slate-900">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <p className="text-sm text-slate-600">{t('scanCompleteReview')}</p>
      {/*
        * Last, and quieter than the rest: it is permission to leave rather than an instruction, and a
        * patient who reads only the heading has still been told the useful part.
        */}
      <p className="text-sm text-slate-500">{t('scanCompleteClose')}</p>
    </Card>
  )
}
